import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  Close,
  Computer,
  Devices,
  Edit,
  Add,
  LocationOn,
  Notes,
  Group
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { useContacts } from '../../hooks/useContacts';
import deviceService from '../../services/deviceService';
import { useAuth } from '../../contexts/AuthContext';

const INITIAL_FORM_STATE = {
  device_id: '',
  nombre: '',
  ubicacion_texto: '',
  ubicacion_url: '',
  device_type: 'switch',
};

const DeviceFormModal = memo(({ 
  open, 
  onClose, 
  onSubmit,
  isEditing,
  entity: editDevice = null,
}) => {
  const { user } = useAuth();
  const { contacts: allContacts, loading: loadingContacts } = useContacts();
  
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const fetchSubscriptions = async (deviceId) => {
      if (!user?.token) return;
      setLoadingSubscriptions(true);
      try {
        const response = await deviceService.getDeviceContactSubscriptions(deviceId, user.token);
        if (response.success) {
          // El backend devuelve un array de contactos en response.data
          const contacts = response.data || [];
          const contactIds = contacts.map(contact => contact.contact_id);
          setSelectedContacts(new Set(contactIds));
        }
      } catch (error) {
        console.error("Error fetching device subscriptions:", error);
      } finally {
        setLoadingSubscriptions(false);
      }
    };

    if (open) {
      if (isEditing && editDevice) {
        setFormData({
          device_id: editDevice.device_id || '',
          nombre: editDevice.nombre || '',
          ubicacion_texto: editDevice.ubicacion_texto || '',
          ubicacion_url: editDevice.ubicacion_url || '',
          device_type: editDevice.device_type || 'switch',
        });
        fetchSubscriptions(editDevice.device_id);
      } else {
        setFormData(INITIAL_FORM_STATE);
        setSelectedContacts(new Set());
      }
      setErrors({});
      setSubmitError('');
    }
  }, [open, isEditing, editDevice, user]);

  const handleContactChange = (contactId) => {
    const newSelection = new Set(selectedContacts);
    if (newSelection.has(contactId)) {
      newSelection.delete(contactId);
    } else {
      newSelection.add(contactId);
    }
    setSelectedContacts(newSelection);
  };
  
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.device_id) tempErrors.device_id = 'El ID del dispositivo es requerido.';
    if (!formData.nombre) tempErrors.nombre = 'El nombre es requerido.';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
  
    try {
      const deviceResponse = await onSubmit(formData, isEditing);
  
      if (deviceResponse && deviceResponse.success) {
        const deviceId = isEditing ? editDevice.device_id : deviceResponse.data.device_id;
        if (deviceId && user?.token) {
          await deviceService.updateDeviceContactSubscriptions(deviceId, Array.from(selectedContacts), user.token);
        }
        handleClose();
      } else {
        setSubmitError(deviceResponse?.message || 'Ocurrió un error al guardar el dispositivo.');
      }
    } catch (error) {
      setSubmitError(error.message || 'Error en la comunicación con el servidor.');
    }
  };

  const handleClose = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setSelectedContacts(new Set());
    setErrors({});
    setSubmitError('');
    onClose();
  }, [onClose]);

  const activeContacts = allContacts.filter(c => c.hasActiveSubscription);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          padding: 4,
          paddingBottom: 3,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Computer sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '1.25rem',
                }}
              >
                {isEditing ? 'Editar Dispositivo' : 'Nuevo Dispositivo'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {isEditing ? 'Modifica los datos del dispositivo' : 'Agrega un nuevo dispositivo eWeLink'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: THEME_COLORS.TEXT_SECONDARY,
              '&:hover': {
                backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                color: THEME_COLORS.TEXT_PRIMARY,
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Contenido */}
      <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
        {/* Error general */}
        {submitError && (
          <Alert 
            severity="error" 
            sx={{ marginBottom: 3 }}
            onClose={() => setSubmitError('')}
          >
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Información del dispositivo */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{
                color: THEME_COLORS.TEXT_PRIMARY,
                fontWeight: 600,
                fontSize: '1rem',
                marginBottom: 2,
              }}
            >
              Información del Dispositivo
            </Typography>
          </Grid>

          {/* Device ID */}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Nombre del Dispositivo"
              placeholder="Ej: Sensor Puerta Principal"
              value={formData.nombre}
              onChange={handleInputChange('nombre')}
              error={!!errors.nombre}
              helperText={errors.nombre || 'Nombre descriptivo para identificar el dispositivo'}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiInputLabel-root': {
                  color: THEME_COLORS.TEXT_SECONDARY,
                },
              }}
            />
          </Grid>

          {/* Tipo de dispositivo */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="device-type-label">Tipo de Dispositivo</InputLabel>
              <Select
                labelId="device-type-label"
                name="device_type"
                value={formData.device_type}
                onChange={handleInputChange('device_type')}
                label="Tipo de Dispositivo"
              >
                <MenuItem value="switch">Switch</MenuItem>
                <MenuItem value="radio_frecuencia">RF</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Device ID */}
          <Grid item xs={12}>
            <TextField
              label="Device ID (de eWeLink)"
              placeholder="Ej: 1000123456"
              value={formData.device_id}
              onChange={handleInputChange('device_id')}
              error={!!errors.device_id}
              helperText={errors.device_id || 'ID único del dispositivo en eWeLink'}
              fullWidth
              required
              disabled={isEditing}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: isEditing ? THEME_COLORS.LIGHT_GRAY : THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiInputLabel-root': {
                  color: THEME_COLORS.TEXT_SECONDARY,
                },
              }}
            />
          </Grid>

          {/* Información de ubicación */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: THEME_COLORS.TEXT_PRIMARY,
                fontWeight: 600,
                fontSize: '1rem',
                marginBottom: 2,
              }}
            >
              Ubicación (Opcional)
            </Typography>
          </Grid>

          {/* Descripción de ubicación */}
          <Grid item xs={12}>
            <TextField
              label="Descripción de Ubicación"
              placeholder="Ej: Oficina Principal, Planta Baja"
              value={formData.ubicacion_texto}
              onChange={handleInputChange('ubicacion_texto')}
              helperText="Descripción textual de la ubicación (opcional)"
              fullWidth
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiInputLabel-root': {
                  color: THEME_COLORS.TEXT_SECONDARY,
                },
              }}
            />
          </Grid>

          {/* URL de ubicación */}
          <Grid item xs={12}>
            <TextField
              label="URL de Ubicación"
              placeholder="https://maps.google.com/..."
              value={formData.ubicacion_url}
              onChange={handleInputChange('ubicacion_url')}
              error={!!errors.ubicacion_url}
              helperText={errors.ubicacion_url || 'Enlace a Google Maps u otro servicio de mapas'}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiInputLabel-root': {
                  color: THEME_COLORS.TEXT_SECONDARY,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Acciones */}
      <DialogActions
        sx={{
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          p: { xs: 2, sm: 3 },
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 120 },
            color: THEME_COLORS.TEXT_SECONDARY,
            borderColor: THEME_COLORS.BORDER_LIGHT,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 140 },
            backgroundColor: THEME_COLORS.TEXT_PRIMARY,
            color: THEME_COLORS.BACKGROUND_PAPER,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#1a1a1a',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {isEditing ? 'Actualizar Dispositivo' : 'Crear Dispositivo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DeviceFormModal.displayName = 'DeviceFormModal';

export default DeviceFormModal; 